/**
 * Компонент указателя ветки (branch label)
 * Отображает название ветки рядом с коммитом
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import type { BranchInfo, NodePosition, GraphConfig } from './types';
import { DEFAULT_GRAPH_CONFIG } from './types';

interface BranchPointerProps {
  branch: BranchInfo;
  position: NodePosition;
  config?: Partial<GraphConfig> | undefined;
  isActive?: boolean | undefined;
  isNew?: boolean | undefined;
  onClick?: (() => void) | undefined;
}

/**
 * Компонент указателя ветки
 */
export const BranchPointer = memo(function BranchPointer({
  branch,
  position,
  config,
  isActive = false,
  isNew = false,
  onClick,
}: BranchPointerProps) {
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };
  const { name, hexColor, isRemote } = branch;

  // Сокращаем название ветки если нужно
  const displayName = name.length > 15 ? name.slice(0, 12) + '...' : name;

  // Ширина прямоугольника зависит от длины текста
  const textWidth = displayName.length * 7 + 12;
  const height = 20;
  const x = position.x + fullConfig.nodeRadius + 8;
  const y = position.y - height / 2;

  return (
    <g
      className="branch-pointer"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Фон метки */}
      <rect
        x={x}
        y={y}
        width={textWidth}
        height={height}
        rx={4}
        fill={hexColor}
        stroke={isActive ? '#ffffff' : 'rgba(255,255,255,0.1)'}
        strokeWidth={isActive ? 1.5 : 0.5}
        className={cn(
          'transition-all duration-200',
          isNew && 'animate-label-appear',
          isActive && 'animate-label-active'
        )}
        style={{
          filter: isActive ? 'brightness(1.2)' : 'none',
        }}
      />

      {/* Иконка remote если нужно */}
      {isRemote && (
        <g transform={`translate(${x + 4}, ${y + 4})`}>
          <circle r={6} fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth={1} />
          <circle r={2} fill="rgba(255,255,255,0.7)" />
        </g>
      )}

      {/* Текст названия ветки */}
      <text
        x={x + (isRemote ? 16 : 6)}
        y={position.y}
        dominantBaseline="middle"
        fill="#ffffff"
        fontSize={11}
        fontFamily="monospace"
        fontWeight={isActive ? 'bold' : 'normal'}
        className="select-none pointer-events-none"
      >
        {displayName}
      </text>

      {/* Индикатор активности (стрелочка) */}
      {isActive && (
        <polygon
          points={`
            ${x - 6},${position.y - 4}
            ${x - 6},${position.y + 4}
            ${x - 2},${position.y}
          `}
          fill={hexColor}
          className="animate-pointer-blink"
        />
      )}
    </g>
  );
});

/**
 * Компонент для отображения всех указателей веток
 */
interface BranchPointersProps {
  branches: Map<string, BranchInfo>;
  branchHeads: Map<string, NodePosition>;
  currentBranch: string | null;
  config?: Partial<GraphConfig>;
  newBranches?: Set<string>;
  onBranchClick?: (branchName: string) => void;
}

export const BranchPointers = memo(function BranchPointers({
  branches,
  branchHeads,
  currentBranch,
  config,
  newBranches = new Set(),
  onBranchClick,
}: BranchPointersProps) {
  const pointers: JSX.Element[] = [];

  branches.forEach((branch, name) => {
    const position = branchHeads.get(name);
    if (!position) return;

    pointers.push(
      <BranchPointer
        key={name}
        branch={branch}
        position={position}
        config={config}
        isActive={name === currentBranch}
        isNew={newBranches.has(name)}
        onClick={onBranchClick ? () => onBranchClick(name) : undefined}
      />
    );
  });

  return <g className="branch-pointers">{pointers}</g>;
});
